import { WebsocketSubscriptionMessage } from './types/outboundWebsocketEvent';

type SubscriptionEntry = {
  request: WebsocketSubscriptionMessage;
  refCount: number;
};

export type SubscriptionAction = 'send' | 'skip';

export class WebsocketSubscriptions {
  private _subscriptions: Map<string, SubscriptionEntry> = new Map();

  private _getKey(request: WebsocketSubscriptionMessage): string {
    switch (request.params.type) {
      case 'conversation':
        return `${request.params.type}:${request.params.csid}`;
      case 'real_time_dashboard':
        return `${request.params.type}:${request.params.dashboard_type}`;
      default:
        return request.params.type;
    }
  }

  getAll(): WebsocketSubscriptionMessage[] {
    return Array.from(this._subscriptions.values()).map(
      (entry) => entry.request,
    );
  }

  getRefCount(key: string): number {
    return this._subscriptions.get(key)?.refCount ?? 0;
  }

  handleRequest(request: WebsocketSubscriptionMessage): SubscriptionAction {
    const key = this._getKey(request);
    const isConversation = request.params.type === 'conversation';

    switch (request.type) {
      case 'connection:subscribe': {
        const existing = this._subscriptions.get(key);
        if (existing) {
          // Conversation subscribes are idempotent: many call sites dispatch
          // `conversationGet` for the same csid (mount, transfer, channel
          // change, retry, reconnect replay) but there is only one matching
          // unsubscribe path (the "Remove" button). Incrementing refCount
          // here would leave a ghost subscription on the server.
          if (isConversation) {
            return 'skip';
          }
          existing.refCount += 1;
          return 'skip';
        }
        this._subscriptions.set(key, { request, refCount: 1 });
        return 'send';
      }
      case 'connection:unsubscribe': {
        const existing = this._subscriptions.get(key);
        if (!existing) {
          return 'skip';
        }
        if (isConversation) {
          this._subscriptions.delete(key);
          return 'send';
        }
        existing.refCount -= 1;
        if (existing.refCount <= 0) {
          this._subscriptions.delete(key);
          return 'send';
        }
        return 'skip';
      }
      default:
        return 'skip';
    }
  }
}

export const websocketSubscriptions = new WebsocketSubscriptions();
